// Messages of the "Competencia" module (competitors, trends, SWOT). Spanish is
// the reference; English is the fallback for the other locales. The competitor
// list / form / timeline keep their original `market.competitors.*` keys.
export const competitionSpanishMessages = {
  competition: {
    title: "Competencia",
    tabs: {
      label: "Secciones de competencia",
      competitors: "Competidores",
      trends: "Tendencias",
      swot: "FODA",
    },
    platforms: {
      x: "X",
      instagram: "Instagram",
      youtube: "YouTube",
      tiktok: "TikTok",
      linkedin: "LinkedIn",
      facebook: "Facebook",
      web: "Web",
      otro: "Otra",
      label: "Red",
    },
    platform_filter: {
      label: "Filtrar por red",
      all: "Todas",
      others: "Web y otras",
    },
    form: {
      save: "Guardar",
      cancel: "Cancelar",
      delete: "Eliminar",
      delete_confirm: "¿Eliminar?",
      none: "Ninguno",
      title_required: "El título es obligatorio",
      title_too_long: "El título es demasiado largo (máx. 200 caracteres)",
      date_invalid: "La fecha no es válida",
      error: "No se pudo guardar. Inténtalo de nuevo.",
      saved: "Guardado",
      deleted: "Eliminado",
      markdown_hint: "Admite listas con «- », **negrita** y enlaces.",
    },
    list: {
      search: "Buscar competidor",
      search_placeholder: "Nombre, ciudad, web…",
      no_results: "Ningún competidor coincide con «%{query}»",
      profiles:
        "%{smart_count} perfil social |||| %{smart_count} perfiles sociales",
      no_profiles: "Sin redes",
      last_activity: "Actividad: %{date}",
      no_activity: "Sin actividad registrada",
      logo_of: "Logo de %{name}",
    },
    detail: {
      back: "Competidores",
      summary: "Resumen",
      analysis: "Análisis",
      strengths: "Puntos fuertes",
      weaknesses: "Puntos débiles",
      conclusion: "Conclusión",
      no_analysis:
        "Sin puntos fuertes, débiles ni conclusión todavía. Añádelos desde «Editar».",
      no_summary: "Sin resumen todavía. Añádelo desde «Editar».",
      timeline: "Cronología",
      related_trends: "Tendencias relacionadas",
      no_related_trends: "Ninguna tendencia ligada a este competidor",
      sections: "Secciones de la ficha",
    },
    social: {
      heading: "Redes sociales",
      add: "Añadir perfil",
      edit: "Editar perfil",
      empty: "Sin perfiles en redes todavía",
      followers: "Seguidores",
      posts: "Publicaciones",
      following: "Seguidos",
      posts_per_week: "Publicaciones/semana",
      engagement: "Engagement",
      measured_on: "Medido el %{date}",
      no_measurement: "Sin mediciones",
      add_measurement: "Añadir medición",
      history: "Histórico",
      history_title: "Histórico de %{handle}",
      history_empty: "Todavía no hay mediciones",
      delta_up: "+%{value} desde el %{date}",
      delta_down: "%{value} desde el %{date}",
      delta_flat: "Sin cambios desde el %{date}",
      chart: "Evolución de seguidores: %{values}",
      open_profile: "Abrir perfil",
      deleted: "Perfil eliminado",
      delete: "Eliminar perfil",
      delete_measurement: "Eliminar medición",
      duplicate: "Ese perfil ya existe para este competidor",
      form: {
        platform: "Red",
        handle: "Usuario",
        handle_hint: "Tal y como se ve, p. ej. «@cuenta» (en web, el dominio).",
        url: "Enlace del perfil",
        url_hint: "Si lo dejas vacío, se calcula a partir del usuario.",
        notes: "Observaciones",
        handle_required: "Escribe el usuario del perfil",
        handle_too_long: "El usuario es demasiado largo (máx. 120 caracteres)",
      },
      snapshot_form: {
        title: "Añadir medición",
        edit_title: "Editar medición",
        measured_at: "Fecha de la medición",
        followers: "Seguidores",
        posts_count: "Publicaciones (total)",
        following_count: "Seguidos",
        posts_per_week: "Publicaciones por semana",
        engagement_rate: "Engagement (%)",
        source: "Fuente",
        source_hint: "Por ejemplo: captura de perfil, manual, agente.",
        notes: "Observaciones",
        date_invalid: "Elige la fecha de la medición",
        integer_invalid:
          "Seguidores, publicaciones y seguidos deben ser números enteros positivos",
        number_invalid:
          "Publicaciones por semana y engagement deben ser números positivos",
        empty: "Escribe al menos una cifra",
        duplicate: "Ya hay una medición de este perfil ese día",
      },
    },
    activity: {
      heading: "Actividad de marketing",
      add: "Añadir actividad",
      edit: "Editar actividad",
      empty: "Sin actividad registrada todavía",
      empty_filtered: "Sin actividad en esta red",
      notes: "Observaciones",
      undated: "Sin fecha",
      open: "Ver publicación",
      kinds: {
        post: "Publicación",
        video: "Vídeo",
        anuncio: "Anuncio",
        campana: "Campaña",
        otro: "Otro",
        label: "Tipo",
      },
      form: {
        title: "Título",
        url: "Enlace",
        published_at: "Fecha de publicación",
        description: "Qué dice o muestra",
        notes: "Observaciones",
      },
    },
    products: {
      heading: "Precios y productos",
      add: "Añadir producto",
      edit: "Editar producto",
      empty: "Sin precios ni productos todavía",
      no_price: "Precio no público",
      per: {
        mes: "/mes",
        ano: "/año",
        pago_unico: "pago único",
        otro: "",
      },
      periods: {
        mes: "Mensual",
        ano: "Anual",
        pago_unico: "Pago único",
        otro: "Otro",
        none: "Sin periodo",
      },
      form: {
        name: "Nombre del producto o plan",
        price: "Precio",
        currency: "Moneda",
        billing_period: "Periodo",
        description: "Qué incluye",
        url: "Página de precios",
        name_required: "El nombre es obligatorio",
        price_invalid: "El precio debe ser un número positivo",
        currency_invalid: "La moneda son tres letras, p. ej. EUR",
      },
    },
    trends: {
      heading: "Tendencias",
      add: "Nueva tendencia",
      edit: "Editar tendencia",
      empty_all: "Todavía no hay tendencias",
      empty_hint:
        "Apunta lo que veas en X, Instagram, YouTube, TikTok o LinkedIn.",
      empty_filtered: "Ninguna tendencia con estos filtros",
      clear_filters: "Quitar filtros",
      detected: "Detectada el %{date}",
      linked: "Competidor: %{name}",
      open: "Ver ejemplo",
      relevance: {
        label: "Relevancia",
        all: "Todas",
        alta: "Alta",
        media: "Media",
        baja: "Baja",
      },
      form: {
        title: "Título",
        description: "Qué está pasando y por qué importa",
        url: "Enlace de ejemplo o fuente",
        detected_at: "Detectada el",
        competitor: "Competidor relacionado",
      },
    },
    swot: {
      heading: "FODA",
      analyses: "Análisis",
      pick: "Elegir análisis",
      add: "Nuevo FODA",
      edit: "Editar FODA",
      delete: "Eliminar FODA",
      deleted: "FODA eliminado",
      empty_all: "Todavía no hay análisis FODA",
      empty_hint:
        "Resume en cuatro cuadrantes cómo está un mercado o un segmento.",
      source: "Fuente: %{source}",
      dated: "Datos del %{date}",
      items: "%{smart_count} punto |||| %{smart_count} puntos",
      empty_quadrant: "Sin puntos",
      add_item: "Añadir punto",
      edit_item: "Editar punto",
      delete_item: "Eliminar punto",
      quadrants: {
        fortaleza: "Fortalezas",
        debilidad: "Debilidades",
        oportunidad: "Oportunidades",
        amenaza: "Amenazas",
        label: "Cuadrante",
      },
      form: {
        title: "Título",
        segment: "Segmento",
        segment_placeholder: "Agencias de IA para pymes en Instagram",
        line: "Línea",
        no_line: "Todo el mercado",
        analysis_date: "Fecha del análisis",
        source: "Fuente o informe",
        source_url: "Enlace a la fuente",
        summary: "Resumen",
      },
      item_form: {
        content: "Punto",
        competitor: "Competidor mencionado",
        content_required: "Escribe el punto",
        content_too_long: "El punto es demasiado largo (máx. 1000 caracteres)",
      },
    },
  },
};

export const competitionEnglishMessages: typeof competitionSpanishMessages = {
  competition: {
    title: "Competition",
    tabs: {
      label: "Competition sections",
      competitors: "Competitors",
      trends: "Trends",
      swot: "SWOT",
    },
    platforms: {
      x: "X",
      instagram: "Instagram",
      youtube: "YouTube",
      tiktok: "TikTok",
      linkedin: "LinkedIn",
      facebook: "Facebook",
      web: "Web",
      otro: "Other",
      label: "Network",
    },
    platform_filter: {
      label: "Filter by network",
      all: "All",
      others: "Web and others",
    },
    form: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      delete_confirm: "Delete?",
      none: "None",
      title_required: "The title is required",
      title_too_long: "The title is too long (max. 200 characters)",
      date_invalid: "The date is not valid",
      error: "Could not save. Please try again.",
      saved: "Saved",
      deleted: "Deleted",
      markdown_hint: "Supports “- ” lists, **bold** and links.",
    },
    list: {
      search: "Search competitors",
      search_placeholder: "Name, city, website…",
      no_results: "No competitor matches “%{query}”",
      profiles:
        "%{smart_count} social profile |||| %{smart_count} social profiles",
      no_profiles: "No social profiles",
      last_activity: "Activity: %{date}",
      no_activity: "No activity recorded",
      logo_of: "%{name} logo",
    },
    detail: {
      back: "Competitors",
      summary: "Summary",
      analysis: "Analysis",
      strengths: "Strengths",
      weaknesses: "Weaknesses",
      conclusion: "Conclusion",
      no_analysis:
        "No strengths, weaknesses or conclusion yet. Add them from “Edit”.",
      no_summary: "No summary yet. Add it from “Edit”.",
      timeline: "Timeline",
      related_trends: "Related trends",
      no_related_trends: "No trend linked to this competitor",
      sections: "Profile sections",
    },
    social: {
      heading: "Social networks",
      add: "Add profile",
      edit: "Edit profile",
      empty: "No social profiles yet",
      followers: "Followers",
      posts: "Posts",
      following: "Following",
      posts_per_week: "Posts/week",
      engagement: "Engagement",
      measured_on: "Measured on %{date}",
      no_measurement: "No measurements",
      add_measurement: "Add measurement",
      history: "History",
      history_title: "%{handle} history",
      history_empty: "No measurements yet",
      delta_up: "+%{value} since %{date}",
      delta_down: "%{value} since %{date}",
      delta_flat: "No change since %{date}",
      chart: "Followers over time: %{values}",
      open_profile: "Open profile",
      deleted: "Profile deleted",
      delete: "Delete profile",
      delete_measurement: "Delete measurement",
      duplicate: "This profile already exists for this competitor",
      form: {
        platform: "Network",
        handle: "Handle",
        handle_hint: "As shown, e.g. “@account” (the domain for a website).",
        url: "Profile link",
        url_hint: "Leave it empty to build it from the handle.",
        notes: "Notes",
        handle_required: "Enter the profile handle",
        handle_too_long: "The handle is too long (max. 120 characters)",
      },
      snapshot_form: {
        title: "Add measurement",
        edit_title: "Edit measurement",
        measured_at: "Measurement date",
        followers: "Followers",
        posts_count: "Posts (total)",
        following_count: "Following",
        posts_per_week: "Posts per week",
        engagement_rate: "Engagement (%)",
        source: "Source",
        source_hint: "For example: profile screenshot, manual, agent.",
        notes: "Notes",
        date_invalid: "Pick the measurement date",
        integer_invalid:
          "Followers, posts and following must be positive whole numbers",
        number_invalid:
          "Posts per week and engagement must be positive numbers",
        empty: "Enter at least one figure",
        duplicate: "This profile already has a measurement on that day",
      },
    },
    activity: {
      heading: "Marketing activity",
      add: "Add activity",
      edit: "Edit activity",
      empty: "No activity recorded yet",
      empty_filtered: "No activity on this network",
      notes: "Notes",
      undated: "Undated",
      open: "View post",
      kinds: {
        post: "Post",
        video: "Video",
        anuncio: "Ad",
        campana: "Campaign",
        otro: "Other",
        label: "Type",
      },
      form: {
        title: "Title",
        url: "Link",
        published_at: "Publication date",
        description: "What it says or shows",
        notes: "Notes",
      },
    },
    products: {
      heading: "Prices and products",
      add: "Add product",
      edit: "Edit product",
      empty: "No prices or products yet",
      no_price: "Price not public",
      per: {
        mes: "/month",
        ano: "/year",
        pago_unico: "one-off",
        otro: "",
      },
      periods: {
        mes: "Monthly",
        ano: "Yearly",
        pago_unico: "One-off payment",
        otro: "Other",
        none: "No period",
      },
      form: {
        name: "Product or plan name",
        price: "Price",
        currency: "Currency",
        billing_period: "Period",
        description: "What it includes",
        url: "Pricing page",
        name_required: "The name is required",
        price_invalid: "The price must be a positive number",
        currency_invalid: "The currency is three letters, e.g. EUR",
      },
    },
    trends: {
      heading: "Trends",
      add: "New trend",
      edit: "Edit trend",
      empty_all: "No trends yet",
      empty_hint:
        "Write down what you see on X, Instagram, YouTube, TikTok or LinkedIn.",
      empty_filtered: "No trend matches these filters",
      clear_filters: "Clear filters",
      detected: "Spotted on %{date}",
      linked: "Competitor: %{name}",
      open: "View example",
      relevance: {
        label: "Relevance",
        all: "All",
        alta: "High",
        media: "Medium",
        baja: "Low",
      },
      form: {
        title: "Title",
        description: "What is happening and why it matters",
        url: "Example or source link",
        detected_at: "Spotted on",
        competitor: "Related competitor",
      },
    },
    swot: {
      heading: "SWOT",
      analyses: "Analyses",
      pick: "Pick an analysis",
      add: "New SWOT",
      edit: "Edit SWOT",
      delete: "Delete SWOT",
      deleted: "SWOT deleted",
      empty_all: "No SWOT analyses yet",
      empty_hint: "Sum up a market or a segment in four quadrants.",
      source: "Source: %{source}",
      dated: "Data from %{date}",
      items: "%{smart_count} item |||| %{smart_count} items",
      empty_quadrant: "No items",
      add_item: "Add item",
      edit_item: "Edit item",
      delete_item: "Delete item",
      quadrants: {
        fortaleza: "Strengths",
        debilidad: "Weaknesses",
        oportunidad: "Opportunities",
        amenaza: "Threats",
        label: "Quadrant",
      },
      form: {
        title: "Title",
        segment: "Segment",
        segment_placeholder: "AI agencies for SMEs on Instagram",
        line: "Line",
        no_line: "Whole market",
        analysis_date: "Analysis date",
        source: "Source or report",
        source_url: "Source link",
        summary: "Summary",
      },
      item_form: {
        content: "Item",
        competitor: "Competitor mentioned",
        content_required: "Write the item",
        content_too_long: "The item is too long (max. 1000 characters)",
      },
    },
  },
};
